import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardBody, Table, Badge, Button, Row, Col } from 'reactstrap';
import moment from 'moment';
import { getMyAccountRequests, AccountRequestSummary } from 'features/matriculates/matriculatesAPI';

const PAGE_SIZE = 10;

const stateLabels: Record<number, { label: string; color: string }> = {
  1: { label: 'Solicitada', color: 'info' },
  2: { label: 'Aceptada', color: 'success' },
  3: { label: 'Rechazada', color: 'danger' },
};

export default function AccountRequestsList() {
  const [rows, setRows] = useState<AccountRequestSummary[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    getMyAccountRequests(page, PAGE_SIZE)
      .then(result => {
        setRows(result.rows ?? []);
        setCount(result.count ?? 0);
      })
      .catch(err => {
        console.error(err);
        setError('No se pudieron recuperar los pedidos de cambio de datos.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [page]);

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <Card>
      <CardHeader>
        <h5 className="mb-0">Pedidos de cambio de datos</h5>
      </CardHeader>
      <CardBody>
        { isLoading && <p>Cargando…</p> }
        { !isLoading && error && <p className="text-danger">{ error }</p> }
        { !isLoading && !error && rows.length === 0 && <p>No hay pedidos previos.</p> }
        { !isLoading && !error && rows.length > 0 && (
          <>
            <Table responsive className="align-items-center">
              <thead>
                <tr>
                  <th>Fecha del pedido</th>
                  <th>Estado</th>
                  <th>Fecha de resolución</th>
                  <th>Motivo de rechazo</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                { rows.map(row => {
                  const state = stateLabels[row.state_id] ?? { label: '-', color: 'secondary' };
                  return (
                    <tr key={row.id}>
                      <td>{ row.created_at ? moment(row.created_at).format('DD/MM/YYYY HH:mm') : '-' }</td>
                      <td><Badge color={state.color}>{ state.label }</Badge></td>
                      <td>{ row.resolved_at ? moment(row.resolved_at).format('DD/MM/YYYY HH:mm') : '-' }</td>
                      <td>{ row.rejection_text || '-' }</td>
                      <td className="text-right">
                        <Link to={`/mi-cuenta/pedidos/${ row.id }`}>
                          <Button color="primary" size="sm">Ver</Button>
                        </Link>
                      </td>
                    </tr>
                  );
                }) }
              </tbody>
            </Table>
            <Row className="align-items-center">
              <Col sm={6}>
                <small>{ count } pedido{ count === 1 ? '' : 's' } en total</small>
              </Col>
              <Col sm={6} className="text-right">
                <Button
                  size="sm"
                  color="secondary"
                  disabled={ page <= 1 || isLoading }
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                >
                  Anterior
                </Button>
                <span className="mx-2">{ page } / { totalPages }</span>
                <Button
                  size="sm"
                  color="secondary"
                  disabled={ page >= totalPages || isLoading }
                  onClick={() => setPage(p => p + 1)}
                >
                  Siguiente
                </Button>
              </Col>
            </Row>
          </>
        )}
      </CardBody>
    </Card>
  );
}
